// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Callout, RowsSkeletonLoader } from '@node-space/ui-components'
import { useDepositAccountsQuery } from 'hooks/queries'
import { useCustomerWalletDepositDetails } from 'pages/NestedCustomers/hooks/useCustomerWalletDepositDetails'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import {
  AccountNumberTypes,
  CustomerWallet,
  Ledger,
  LedgerWalletType,
  NestedCustomerDetails,
} from 'types/customers'
import { Currencies, WalletType } from 'types/types'
import MultipleDepositDetails from '../Deposit/components/MultipleDepositDetails'
import SingleDepositDetails from '../Deposit/components/SingleDepositDetails'

interface FiatDepositDetailsProps {
  wallet: WalletType
  customer?: NestedCustomerDetails
  isFetchingWallets: boolean
  customerWallet?: CustomerWallet
}

export const FiatDepositDetails = ({
  wallet,
  isFetchingWallets,
  customer,
  customerWallet,
}: FiatDepositDetailsProps) => {
  const { t } = useTranslation()

  const {
    data: principleWalletDepositDetails,
    isError: isDepositDetailsError,
    isFetching: isFetchingDepositDetails,
  } = useDepositAccountsQuery(wallet?.id?.toString(), !!wallet && !customer)

  const { customerDepositDetails, isFetchingCountries } = useCustomerWalletDepositDetails(customer)

  const depositDetails = useMemo(() => {
    if (customerDepositDetails && customerWallet) {
      const fiatLedger: Ledger = customerWallet?.ledgers?.find(ledger => {
        if (customerWallet?.balance?.currencyCode === Currencies.GBP) {
          return (
            ledger?.type === LedgerWalletType.FIAT &&
            ledger?.accountNumberFormat === AccountNumberTypes.SCAN
          )
        }

        return ledger?.type === LedgerWalletType.FIAT
      })

      if (fiatLedger) {
        const accountHolderName = fiatLedger?.accountHolderName || customerDepositDetails?.name

        return [
          {
            currency: customerWallet?.balance?.currencyCode,
            accountHolderName: accountHolderName,
            accountNumber: fiatLedger?.accountNumber,
            bankCode: fiatLedger?.code,
            reference: fiatLedger?.paymentReference,
            accountNumberFormat: fiatLedger?.accountNumberFormat,
            isSwift: fiatLedger?.accountNumberFormat === AccountNumberTypes.SWIFT,
            beneficiaryAddress: customerDepositDetails?.address,
          },
        ] as DepositBankAccount[]
      }
    }
  }, [customerDepositDetails, customerWallet])

  const details = useMemo(() => {
    if (customerWallet) {
      return depositDetails
    }

    return principleWalletDepositDetails
  }, [principleWalletDepositDetails, depositDetails, customerWallet])

  const isFetchingDetails = useMemo(() => {
    return isFetchingDepositDetails || isFetchingWallets || isFetchingCountries
  }, [isFetchingDepositDetails, isFetchingWallets, isFetchingCountries])

  return (
    <Box width="full" className="my-6 space-y-4 text-gray-100">
      {isFetchingDetails && (
        <Box className="gap-0">
          <RowsSkeletonLoader rows={6} />
        </Box>
      )}

      {!isFetchingDetails && (isDepositDetailsError || !details?.length) && (
        <Box flex width={'full'} paddingT={8}>
          <Callout variant="error">{t('oopsSomethingWentWrongFetchDespoitDetails')}</Callout>
        </Box>
      )}

      {!isFetchingDetails && !isDepositDetailsError && !!details?.length && (
        <Box paddingT={8}>
          {details?.length > 1 ? (
            <MultipleDepositDetails bankAccounts={details} wallet={wallet} />
          ) : (
            <Box>
              <SingleDepositDetails account={details?.[0]} wallet={wallet} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
